import type { BilingualRecord, BilingualText } from "./bilingualContent";

function contentText(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasAnyKey(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((key) => key in value);
}

function hasRequiredTextFields(value: Record<string, unknown>, fields: string[]): boolean {
  return fields.every((field) => isNonEmptyText(value[field]));
}

function hasValidScoreRange(value: Record<string, unknown>): boolean {
  return Number.isFinite(Number(value.minScore)) && Number.isFinite(Number(value.maxScore));
}

function arraysHaveSameLength(left: unknown, right: unknown): boolean {
  if (!Array.isArray(left) && !Array.isArray(right)) return true;
  return Array.isArray(left) && Array.isArray(right) && left.length === right.length;
}

function requiredArrayFieldsHaveSameShape(left: unknown, right: unknown): boolean {
  if (!isRecord(left) || !isRecord(right)) return true;
  const arrayFields = [
    "signals",
    "sourceTerms",
    "sourceDepartments",
    "typicalCharacteristics",
    "risks",
    "relevantDepartments",
    "crmDataHints"
  ];
  return arrayFields.every((field) => {
    if (!(field in left) && !(field in right)) return true;
    return arraysHaveSameLength(left[field], right[field]);
  });
}

function requiredTextFieldsHaveSameShape(left: unknown, right: unknown): boolean {
  if (!isRecord(left) || !isRecord(right)) return true;
  const textFields = ["name", "description", "meaning", "recommendation", "segmentName", "unit"];
  return textFields.every((field) => {
    if (!(field in left) && !(field in right)) return true;
    return isNonEmptyText(left[field]) && isNonEmptyText(right[field]);
  });
}

function segmentScoresMatch(left: unknown, right: unknown): boolean {
  if (!isRecord(left) || !isRecord(right)) return true;
  if (!("minScore" in left) && !("maxScore" in left) && !("minScore" in right) && !("maxScore" in right)) return true;
  return Number(left.minScore) === Number(right.minScore) && Number(left.maxScore) === Number(right.maxScore);
}

function sameStructure(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!arraysHaveSameLength(left, right)) return false;
    return (left as unknown[]).every((item, index) => sameStructure(item, (right as unknown[])[index]));
  }

  if (!isRecord(left) || !isRecord(right)) return true;
  return requiredTextFieldsHaveSameShape(left, right) && requiredArrayFieldsHaveSameShape(left, right) && segmentScoresMatch(left, right);
}

/**
 * Generic customer-value content validation.
 *
 * This deliberately does not translate or map customer-defined labels via word lists.
 * It only checks whether the stored language side is structurally usable:
 * missing, empty, identical or incomplete language sides must be repaired server-side
 * through the translation/write path.
 */
export function hasUsableCustomerValueContent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0 && value.every((item) => hasUsableCustomerValueContent(item));
  if (!isRecord(value)) return true;

  const isCriterionLike = hasAnyKey(value, ["name", "description", "signals", "sourceTerms", "sourceDepartments", "unit"]);
  const isSegmentLike = hasAnyKey(value, ["minScore", "maxScore"]);
  const isScoreTemplateLike = !isSegmentLike && hasAnyKey(value, ["segmentName", "recommendation"]);

  if (isSegmentLike) {
    if (!hasRequiredTextFields(value, ["name", "description"]) || !hasValidScoreRange(value)) return false;
    for (const field of ["meaning", "recommendation"]) {
      if (field in value && !isNonEmptyText(value[field])) return false;
    }
    for (const field of ["typicalCharacteristics", "risks", "relevantDepartments", "crmDataHints"]) {
      if (field in value && (!Array.isArray(value[field]) || !(value[field] as unknown[]).every(isNonEmptyText))) return false;
    }
  } else if (isCriterionLike && !hasRequiredTextFields(value, ["name", "description"])) {
    return false;
  }
  if (isScoreTemplateLike && !hasRequiredTextFields(value, ["segmentName", "recommendation"])) return false;

  const entries = Object.values(value);
  if (!entries.length) return false;

  return entries.some((entry) => {
    if (Array.isArray(entry)) return entry.length === 0 || entry.some((item) => hasUsableCustomerValueContent(item));
    return hasUsableCustomerValueContent(entry);
  });
}

/**
 * Kept as compatibility API for older guards.
 * This must not contain customer-defined labels such as Platin/Gold/Silber,
 * because customer content is not repaired through word lists. Missing,
 * empty, identical or structurally invalid language sides are repaired by
 * the server-side translation path instead.
 */
export function containsForbiddenGermanCustomerValueTerms(value: unknown): boolean {
  void value;
  return false;
}

export function sameContent(left: unknown, right: unknown): boolean {
  return contentText(left) === contentText(right);
}

export function bilingualRecordNeedsCustomerValueRepair<T>(record: BilingualRecord<T> | undefined): boolean {
  const de = record?.de;
  const en = record?.en;
  if (!hasUsableCustomerValueContent(de) || !hasUsableCustomerValueContent(en)) return true;
  if (!sameStructure(de, en)) return true;
  return sameContent(de, en);
}

export function bilingualTextNeedsCustomerValueRepair(record: BilingualText | undefined): boolean {
  if (!record?.de?.trim() || !record.en?.trim()) return true;
  return record.de.trim() === record.en.trim();
}
